import React from "react";
import { ChevronUp, Filter } from "lucide-react";
import ToTitleCase from "utils/ToTitleCase";
import infiniteLoader from "../assets/loaders/InfinityLoader.gif";
import ButtonTertiary from "shared/Button/ButtonTertiary";
import { ISidebarFilters } from "Interfaces/interface.filter";
import ToolsDropdown from "@/pages/products/ToolsDropdown";
import { IFacetItem } from "Interfaces/interface.facet";
import { ICategory } from "@/Interfaces/interface.productsapi";
import { useAppSelector } from "@/store/hooks/hooks";
import { selectProductStatus } from "@/store/slices/productsSlice";
import ExploreSidebarCard from "@/shared/ExploreSidebarCard/ExploreSidebarCard";
import { Package } from "lucide-react";

const MobileFilterSidebar: React.FC<ISidebarFilters> = ({
  clearFilter,
  filterData,
  showClear,
  handleFilter,
  facets = [],
  loading,
  categories,
  onOpenModal,
  selectedCategoryIds = [],
  selectedSubCategoryIds = [],
  selectedBrandIds = [],
  onCategoryChange,
  onSubCategoryChange,
  onBrandChange,
}) => {
  const productsLoading = useAppSelector(selectProductStatus);
  const checkDisable = productsLoading === "loading";

  const isSelected = (facetName: string) => {
    const lowerName = facetName.toLowerCase();

    if (lowerName === "category") {
      return (
        selectedCategoryIds.length > 0 || selectedSubCategoryIds.length > 0
      );
    }

    if (lowerName === "brand") {
      return selectedBrandIds.length > 0;
    }

    if (lowerName === "price") {
      const predefinedSelections = facets
        ?.find((f) => f.name === facetName)
        ?.values.some((v: any) => filterData.includes(v.id));

      const customPriceSelections = filterData.some((id) => {
        if (!id.includes("-")) return false;

        const existsInAnyFacet = facets?.some((f) =>
          f.values.some((v) => v.id === id)
        );

        return !existsInAnyFacet;
      });

      return predefinedSelections || customPriceSelections;
    }

    return (
      facets
        ?.find((f) => f.name === facetName)
        ?.values.some((v: any) => filterData.includes(v.id)) || false
    );
  };

  const getSelectedCount = (facetName: string) => {
    const lowerName = facetName.toLowerCase();

    if (lowerName === "category") {
      return selectedCategoryIds.length + selectedSubCategoryIds.length;
    }

    if (lowerName === "brand") {
      return selectedBrandIds.length;
    }

    if (lowerName !== "price") {
      return (
        facets
          ?.find((f) => f.name === facetName)
          ?.values.filter((v: any) => filterData.includes(v.id)).length || 0
      );
    }

    const predefinedCount =
      facets
        ?.find((f) => f.name === facetName)
        ?.values.filter((v) => filterData.includes(v.id)).length || 0;

    const customPriceCount = filterData.filter((id) => {
      if (!/^\d+-\d+$/.test(id)) return false;

      const existsInAnyFacet = facets?.some((f) =>
        f.values.some((v) => v.id === id)
      );

      return !existsInAnyFacet;
    }).length;

    return predefinedCount + customPriceCount;
  };

  return (
    <div className="w-full border-b border-gray-200 bg-white px-4 shadow-sm sticky top-0 z-30 py-1">
      {loading ? (
        <div className="flex justify-center items-center">
          <img src={infiniteLoader} alt="Loading..." className="w-[12%]" />
        </div>
      ) : (
        <div className="flex items-center gap-3 overflow-x-auto no-scrollbar mt-5">
          <div className="flex items-center gap-2 pr-4 border-r border-gray-300">
            {!showClear && (
              <Filter
                size={18}
                className={`text-gray-700 ${filterData.length > 0 && "hidden"}`}
              />
            )}
            {showClear && (
              <ButtonTertiary
                disabled={checkDisable}
                onClick={clearFilter}
                variant="cancel"
              >
                <Filter size={18} />
                Clear
              </ButtonTertiary>
            )}
          </div>

          {facets.map((facet: IFacetItem) => {
            const selectedCount = getSelectedCount(facet.name);
            return (
              <ButtonTertiary
                key={facet.name}
                onClick={() => onOpenModal?.(facet)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm whitespace-nowrap font-semibold transition-all duration-200 shadow-sm ${isSelected(facet.name)
                  ? "text-indigo-400 border-2 border-indigo-400"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                <span className="flex items-center gap-1">
                  <ToTitleCase text={facet.name} />
                  {selectedCount > 0 && (
                    <span className="bg-indigo-500 text-white text-[10px] font-semibold px-2 rounded-full">
                      {selectedCount}
                    </span>
                  )}
                </span>
                <ChevronUp size={14} />
              </ButtonTertiary>
            );
          })}
        </div>
      )}
      <ToolsDropdown />
      <ExploreSidebarCard to="/curated-kits" icon={Package} text="Explore Curated Kits" />
    </div>
  );
};

export default MobileFilterSidebar;
